import Box from "@mui/material/Box";
import Button, { ButtonProps } from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { visuallyHidden } from "@mui/utils";
import { ReactNode } from "react";

export interface LoadingButtonProps extends ButtonProps {
  loading?: boolean;
  label: string;
  icon?: ReactNode;
}

/**
 * A themed `Button` that swaps its label for a spinner while `loading`. The label stays in the
 * accessibility tree (visually hidden) and the button reports `aria-busy`; the spinner uses
 * `currentColor` so it reads on contained, outlined and text buttons alike.
 */
const LoadingButton = ({
  label,
  loading = false,
  icon,
  ...rest
}: LoadingButtonProps) => (
  <Button {...rest} aria-busy={loading} disabled={rest.disabled || loading}>
    {loading ? (
      <>
        <CircularProgress
          aria-hidden
          color="inherit"
          size={20}
          thickness={5}
          sx={{ color: "currentColor" }}
        />
        <Box component="span" sx={visuallyHidden}>
          {label}
        </Box>
      </>
    ) : (
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
      >
        {label}
        {icon}
      </Box>
    )}
  </Button>
);

export default LoadingButton;
